import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AgentPromptService } from '../../agent-prompt.service';
import { AgentPrompt } from '../../agent-prompt.models';
import { LiveCallModelsService } from '../../../models/live-call-models.service';
import { LiveCallModel } from '../../../models/live-call-models.models';
import { PromptHistoryPanelComponent } from '../prompt-history-panel/prompt-history-panel.component';

export type PromptPanelMode = 'create' | 'edit';

/** Models grouped for the picker's optgroups. */
interface ModelGroup {
  provider: string;
  models: LiveCallModel[];
}

/**
 * Create or edit one live-call agent PROMPT. Editing is VERSIONED: every save
 * appends an immutable snapshot with a required "what changed" note (the
 * Editor tab), and the History tab lists all versions with restore + a
 * side-by-side diff against the current text. Old versions are never edited
 * or deleted.
 *
 * Prompts are addressed by id. Listings only carry a preview of the prompt
 * text, so edit mode fetches the full text before the editor becomes
 * writable — system prompts run to thousands of lines. The Active toggle
 * lives on the listing row (PATCH metadata), not in the versioned editor.
 */
@Component({
  selector: 'app-prompt-form-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, PromptHistoryPanelComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './prompt-form-panel.component.html',
})
export class PromptFormPanelComponent implements OnInit, OnChanges {
  @Input() mode: PromptPanelMode = 'create';
  @Input() prompt: AgentPrompt | null = null;
  /** Which tab the panel opens on — the listing's History shortcut passes 'history'. */
  @Input() initialTab: 'editor' | 'history' = 'editor';

  @Output() saved = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  private promptService = inject(AgentPromptService);
  private modelsService = inject(LiveCallModelsService);

  /** Edit mode only: Editor (versioned save) or History (versions + diff). */
  activeTab = signal<'editor' | 'history'>('editor');

  submitting = signal(false);
  errorMessage = signal('');
  /** True while edit mode fetches the full prompt text. */
  loadingPrompt = signal(false);

  /** Selectable voice models, grouped by provider for the picker. */
  modelGroups = signal<ModelGroup[]>([]);
  modelsLoading = signal(false);
  private defaultModelId: number | null = null;

  form = {
    name: '',
    system_prompt: '',
    /** '' = no model chosen (native selects bind numbers as strings). */
    model_id: '' as string | number,
    is_active: true,
    /** Required in edit mode — recorded on the new version. */
    change_notes: '',
  };

  ngOnInit(): void {
    this.loadModels();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['prompt'] || changes['mode']) {
      this.errorMessage.set('');
      this.activeTab.set(this.mode === 'edit' ? this.initialTab : 'editor');
      if (this.mode === 'edit' && this.prompt) {
        this.form = {
          name: this.prompt.name ?? '',
          system_prompt: this.prompt.systemPrompt ?? '',
          model_id: this.prompt.modelId ?? '',
          is_active: this.prompt.isActive ?? true,
          change_notes: '',
        };
        this.fetchFullPrompt(this.prompt.id);
      } else {
        this.form = {
          name: '',
          system_prompt: '',
          model_id: this.defaultModelId ?? '',
          is_active: true,
          change_notes: '',
        };
      }
    }
  }

  /** Live size of the prompt being edited, shown under the editor. */
  promptChars(): number {
    return this.form.system_prompt.length;
  }

  promptLines(): number {
    return this.form.system_prompt ? this.form.system_prompt.split('\n').length : 0;
  }

  onSubmit(): void {
    this.errorMessage.set('');
    if (!this.form.name.trim()) return this.errorMessage.set('Name is required.');
    if (!this.form.system_prompt.trim()) return this.errorMessage.set('The system prompt is empty.');
    if (this.mode === 'edit' && !this.form.change_notes.trim()) {
      return this.errorMessage.set('Describe what changed — the note is saved with the new version.');
    }

    this.submitting.set(true);
    const done = {
      next: () => {
        this.submitting.set(false);
        this.saved.emit();
      },
      error: (err: any) => {
        this.submitting.set(false);
        this.errorMessage.set(err?.message ?? 'Save failed.');
      },
    };

    const modelId = this.form.model_id === '' ? null : Number(this.form.model_id);

    if (this.mode === 'create') {
      this.promptService
        .createPrompt({
          name: this.form.name.trim(),
          system_prompt: this.form.system_prompt,
          model_id: modelId,
          is_active: this.form.is_active,
        })
        .subscribe(done);
      return;
    }

    if (!this.prompt) {
      this.submitting.set(false);
      return this.errorMessage.set('No prompt selected to edit.');
    }
    // Versioned save: the backend bumps the head and appends the snapshot atomically.
    this.promptService
      .saveVersion(this.prompt.id, {
        name: this.form.name.trim(),
        system_prompt: this.form.system_prompt,
        model_id: modelId,
        change_notes: this.form.change_notes.trim(),
      })
      .subscribe(done);
  }

  /** A restore moved the head forward — hand control back so the list refreshes. */
  onRestored(): void {
    this.saved.emit();
  }

  onClose(): void {
    this.closed.emit();
  }

  /** Provider heading for an optgroup: "google" reads better as "Gemini". */
  providerLabel(provider: string): string {
    if (provider === 'google') return 'Gemini (Google)';
    if (provider === 'openai') return 'OpenAI';
    return provider;
  }

  /** Listings carry only a preview; pull the full text so the editor is truthful. */
  private fetchFullPrompt(promptId: number): void {
    this.loadingPrompt.set(true);
    this.promptService.getPrompt(promptId).subscribe({
      next: (res) => {
        // Only overwrite if the user has not started typing over the preview.
        this.form.system_prompt = res.prompt.systemPrompt ?? this.form.system_prompt;
        this.loadingPrompt.set(false);
      },
      error: (err) => {
        this.loadingPrompt.set(false);
        this.errorMessage.set(err?.message ?? 'Could not load the full prompt text.');
      },
    });
  }

  /** Active models only — a prompt should not newly target a disabled model. */
  private loadModels(): void {
    this.modelsLoading.set(true);
    this.modelsService.listModels(false).subscribe({
      next: (res) => {
        const models = res.models ?? [];
        this.defaultModelId = models.find((m) => m.isDefault)?.id ?? null;
        this.modelGroups.set(this.groupByProvider(models));
        // Preselect the in-use model for brand-new prompts.
        if (this.mode === 'create' && this.form.model_id === '' && this.defaultModelId !== null) {
          this.form.model_id = this.defaultModelId;
        }
        this.modelsLoading.set(false);
      },
      error: () => {
        // The picker degrades to "no model" — the prompt can still be saved.
        this.modelGroups.set([]);
        this.modelsLoading.set(false);
      },
    });
  }

  private groupByProvider(models: LiveCallModel[]): ModelGroup[] {
    const groups = new Map<string, LiveCallModel[]>();
    for (const model of models) {
      const group = groups.get(model.provider) ?? [];
      group.push(model);
      groups.set(model.provider, group);
    }
    return Array.from(groups.entries()).map(([provider, grouped]) => ({ provider, models: grouped }));
  }
}
